/* ==========================================================================
   pdp-lightbox.js :: snippets/pdp-lightbox.liquid behaviour (2026-08-22)

   Tap any PDP infographic static to enlarge it. No library.

   Scope: the seven pdp-static-0X.png slides inside .pdp-gallery__item. The
   64px thumbnail buttons in the strip are NOT included -- they are gallery
   navigation and hijacking them would break the carousel.

   Overlay grammar is lifted from assets/email-popup.js on purpose: Escape +
   backdrop close, focus moves to the close button on open and returns to the
   image on close, focus stays trapped inside while open, Ordin.lenis
   stop/start null-safe, transitions skipped under prefers-reduced-motion.

   EXCEPT the scroll lock (2026-08-27): html.popup-lock is applied on mobile
   ONLY. See the TWO-MODE SCROLL LOCK comment below -- on desktop that class
   kills the sticky PDP gallery behind this translucent backdrop.

   Full resolution: the slides are served at &width=1200 (and a 600/900/1200
   srcset). The overlay strips every width/height/crop param off the asset
   URL so it loads the original 1600x1600 PNG.
   ========================================================================== */
(function () {
  'use strict';

  var box = document.querySelector('[data-pdp-lightbox]');
  if (!box) return;

  var frameImg = box.querySelector('[data-lightbox-img]');
  var frame = box.querySelector('.pdp-lightbox__frame');
  var closeBtn = box.querySelector('.pdp-lightbox__close');
  var track = document.querySelector('[data-gallery-track], [data-nb-track]'); /* round 13: the Notebook PDP gallery too */
  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var lastFocus = null;

  /* 2026-10-25 (founder, PostHog): EVERY gallery photo opens here (not only the pdp-static cards), and the
     overlay zooms: pinch + double-tap on phones, click-to-zoom with mouse panning on desktop; unzoomed, a
     sideways swipe (or the arrows / arrow keys) moves to the next visible gallery photo and a downward swipe
     closes. Review photos are NOT handled here any more: assets/review-photo-popup.js shows them with the
     full review. Delegated, so slides shown later (bundle / colour switches) work too. */
  var GALLERY_IMG = '.pdp-gallery__item:not(.pcb-kitslide):not(.pcb-rvv) img';
  /* 2026-10-27 (founder): Adam L.'s review slide (.pcb-rvv: review card over a looping video) opens here too, as a STATIC
     slide -- the card over the video's poster frame. No <video> is ever cloned into the overlay, and zoom is off for it
     (a text card has nothing to inspect). Before this it was excluded above AND pdp.js's video click handler
     (stopPropagation + play/pause toggle) swallowed the tap, so it only paused the video. */
  var RVV = '.pdp-gallery__item.pcb-rvv';
  var HIT = '.pdp-lightbox__img, .pdp-lightbox__rvv';
  function isRvv(n) { return !!(n && n.matches && n.matches(RVV)); }
  function galleryList() {
    if (!track) return [];
    return Array.prototype.slice.call(track.querySelectorAll(GALLERY_IMG + ', ' + RVV)).filter(function (im) { return im.offsetParent !== null && !im.closest('[hidden]'); });
  }
  var list = [], at = 0;

  /* Strip Shopify's CDN sizing params so the overlay gets the original file.
     Keeps ?v= (the cache key) so the browser can reuse what it already has. */
  function fullRes(src) {
    try {
      var u = new URL(src, window.location.href);
      ['width', 'height', 'crop', 'pad_color', 'format'].forEach(function (p) {
        u.searchParams.delete(p);
      });
      return u.toString();
    } catch (e) {
      return src.split('&width=')[0];
    }
  }

  function setLenis(stop) {
    var lenis = window.Ordin && window.Ordin.lenis;
    if (!lenis) return;
    if (stop) { lenis.stop(); } else { lenis.start(); }
  }

  /* ------------------------------------------------------------------
     TWO-MODE SCROLL LOCK (ported from assets/cart.js -- the full
     derivation lives in that file's lockScroll comment). Short version:

     1. STICKY vs OVERFLOW. base.css sets html{overflow-x:clip} and
        body{overflow-x:clip}, so body is its own scroll container and
        html{overflow:hidden} -- which is the ONLY thing html.popup-lock
        does (components.css:2482) -- makes the nearest scrollport of
        .pdp-gallery unscrollable and DEACTIVATES its position:sticky
        (pdp.css:201-207, @media (min-width:900px)). The gallery snaps
        ~1000px back to its in-flow position. That matters more here
        than anywhere else in the theme: this overlay opens FROM the
        gallery, so the element that vanishes is the image the visitor
        just clicked, and the backdrop is only rgb(26 26 26 / 0.82) --
        translucent enough to see it go. At >=900px we block background
        scroll with EVENTS and never touch html/body overflow.

     2. MOBILE SAFARI. Below the breakpoint the html.popup-lock idiom is
        kept EXACTLY as it was: Lenis is null on touch, a class hook
        alone will not stop native momentum scroll, and .pdp-gallery is
        position:relative there (pdp.css:190) so there is no sticky to
        break.

     WHY popup-lock IS MOBILE-ONLY: its one and only consumer in the
     whole theme is assets/components.css:2482
     -> html.popup-lock{overflow:hidden}, i.e. the very rule that breaks
     sticky. No other rule keys off the class, so applying it on desktop
     would buy nothing and cost the gallery. (assets/email-popup.js still
     adds it unconditionally -- that overlay is an opaque full takeover
     with nothing of the PDP visible behind it, and it is deliberately
     out of scope here.) If a desktop-safe hook is ever needed, scope
     that rule to max-width:899px and the class can go in both branches.

     THREE COPIES OF THIS BREAKPOINT NOW EXIST -- assets/cart.js
     (STICKY_MQ), assets/pdp.js, and this file. If the .pdp-gallery
     sticky media query in assets/pdp.css ever moves, all three move.

     The mode chosen at lock time is recorded in lockedMode and released
     by exactly that branch, so a resize across the breakpoint while the
     overlay is open can never leak the other mode's state.
     ------------------------------------------------------------------ */
  var STICKY_MQ = '(min-width: 900px)';
  var scrollLocked = false;
  /* 'events' (>=900px, sticky-safe) | 'popup-lock' (<900px, mobile Safari) */
  var lockedMode = null;
  var lockHandlers = null;

  /* Allow-list the PANEL, never the overlay root. .pdp-lightbox is
     position:fixed inset:0 (components.css:2499) and its first child
     .pdp-lightbox__backdrop is absolute inset:0 (:2518) with no
     pointer-events:none -- so box.contains() would report "inside the
     overlay" for nearly the whole viewport and the lock would never
     engage. .pdp-lightbox__frame (the <figure>) is the real scroller:
     overflow:auto + overscroll-behavior:contain (:2524-2539), and below
     900px it is the pan surface for the enlarged image. */
  function inPanel(node) {
    if (!node) return false;
    var scope = frame || box; /* null-safe: markup change */
    return !!(scope && scope.contains(node));
  }

  function isTextEntry(node) {
    if (!node || !node.tagName) return false;
    var tag = node.tagName;
    return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' ||
      node.isContentEditable === true;
  }

  /* The close button is a SIBLING of the frame, not a descendant
     (snippets/pdp-lightbox.liquid), and it is the only focusable node in
     the overlay. Swallowing Space there would break Space-to-close, so
     controls inside the overlay root are exempt from the key block. */
  function isOverlayControl(node) {
    if (!node || !node.closest) return false;
    var ctrl = node.closest('button, a[href], [role="button"]');
    return !!(ctrl && box.contains(ctrl));
  }

  function lockScroll() {
    if (scrollLocked) return;
    scrollLocked = true;
    /* 2026-10-27: shared events lock at every width (assets/scroll-lock.js);
       html.popup-lock / overflow:hidden is gone -- it let iPhone Safari
       scroll the page under the lightbox and drag the fixed overlay with it. */
    if (window.OrdinScrollLock) window.OrdinScrollLock.lock('lightbox', frame || box);
  }

  function unlockScroll() {
    if (!scrollLocked) return;
    scrollLocked = false;
    if (window.OrdinScrollLock) window.OrdinScrollLock.unlock('lightbox');
  }

  /* ==================================================================
     2026-10-25 round 12 (founder): the photos sit side by side in a native scroll-snap strip -- flick with real momentum
     and snap, no arrow buttons (memory: horizontal-scroll-native-feel). Round 11's IM8 look stays: blurred page behind,
     the photo pops out of its spot (FLIP ~300 ms), round ivory X outside the photo's top-right corner, closes on backdrop
     tap / X / Esc / swipe down. Zoom (pinch, double-tap, click + mouse pan, trackpad pinch) works on the photo in view;
     while zoomed the strip stops scrolling so the finger pans the photo instead.
     ================================================================== */
  var strip = document.createElement('div');
  strip.className = 'pdp-lightbox__strip';
  frame.appendChild(strip);
  if (frameImg) frameImg.remove();           /* the single <img> is replaced by one per slide */
  var srcEl = null, slides = [];

  /* the element that pops out / carries the X: the <img>, or the static review card box */
  function media(s) { return s ? (s.querySelector('.pdp-lightbox__rvv') || s.querySelector('img')) : null; }
  function innerImg(el) { return el && (el.tagName === 'IMG' ? el : el.querySelector('img')); }
  function ready(el) { var im = innerImg(el); return !!(im && im.complete && im.naturalWidth); }
  function zoomable() { return !!(frameImg && frameImg.tagName === 'IMG'); }
  function imgRect() { return frameImg ? frameImg.getBoundingClientRect() : { width: 0 }; }
  /* 2026-10-27 (founder, iPhone): the X used to show at its CSS spot (top), jump mid-screen, then settle -- it was placed
     before the photo had loaded and then against the photo's SCALED rect during the pop-in (scroll + load events fire
     mid-FLIP). Now it is placed from the photo's untransformed layout box (offsetWidth/Height, with the live transform's
     translate taken back out; transform-origin is 0 0), stays invisible until the photo has loaded and that box is known,
     then fades in on opacity only. Position never transitions, so a resize / slide change moves it in one frame. */
  function layoutBox(im) {
    if (!ready(im)) return null;
    var w = im.offsetWidth, h = im.offsetHeight; if (!w || !h) return null;
    var r = im.getBoundingClientRect(), e = 0, f = 0, t = window.getComputedStyle(im).transform;
    if (t && t !== 'none' && window.DOMMatrixReadOnly) { try { var M = new DOMMatrixReadOnly(t); e = M.e; f = M.f; } catch (x) {} }
    var l = r.left - e, tp = r.top - f;
    return { left: l, top: tp, right: l + w, bottom: tp + h, width: w, height: h };
  }
  function placeX() {
    /* the X sits just ABOVE the photo's top-right corner, its right edge on the photo's right edge, clamped on screen.
       Hit area 52px (pdp.css ::after). */
    if (!closeBtn || !box.classList.contains('is-open')) return;
    /* mid pop-in the box is already final; re-measuring through the running transform only adds sub-pixel jitter */
    if (closeBtn.classList.contains('is-placed') && frameImg && frameImg.getAnimations && frameImg.getAnimations().length) return;
    var r = layoutBox(frameImg); if (!r) return;
    var vw = document.documentElement.clientWidth || window.innerWidth, vh = window.innerHeight;
    var bw = closeBtn.offsetWidth || 40, gap = 8, m = 8;
    var left = Math.round(Math.max(m, Math.min(vw - bw - m, r.right - bw)));
    var top = Math.round(Math.max(m, Math.min(vh - bw - m, r.top - bw - gap)));
    if (closeBtn.style.left !== left + 'px') closeBtn.style.left = left + 'px';
    if (closeBtn.style.top !== top + 'px') closeBtn.style.top = top + 'px';
    if (!closeBtn.classList.contains('is-placed')) { void closeBtn.offsetWidth; closeBtn.classList.add('is-placed'); }
  }
  function flip(from, back, done) {
    if (reduced || !from || !from.width || !frameImg || !frameImg.animate) { if (done) done(); return; }
    var to = imgRect(); if (!to.width) { if (done) done(); return; }
    var k = 'translate(' + (from.left - to.left) + 'px,' + (from.top - to.top) + 'px) scale(' + (from.width / to.width) + ',' + (from.height / to.height) + ')';
    var kf = [{ transform: k, transformOrigin: '0 0', borderRadius: '6px' }, { transform: 'none', transformOrigin: '0 0' }];
    var a = frameImg.animate(back ? kf.reverse() : kf, { duration: back ? 240 : 300, easing: back ? 'cubic-bezier(.4,0,.6,1)' : 'cubic-bezier(.2,.8,.2,1)', fill: back ? 'forwards' : 'none' });
    /* round 22 (founder): the dim/blur and the pop-out start on the same frame with the same duration and curve */
    var bd = box.querySelector('.pdp-lightbox__backdrop'), tm = { duration: back ? 240 : 300, easing: back ? 'cubic-bezier(.4,0,.6,1)' : 'cubic-bezier(.2,.8,.2,1)', fill: 'forwards' };
    if (bd && bd.animate) { var ba = bd.animate(back ? [{ opacity: 1 }, { opacity: 0 }] : [{ opacity: 0 }, { opacity: 1 }], tm); bd.style.opacity = ''; if (!back) ba.onfinish = function () { ba.cancel(); }; }
    a.onfinish = function () { if (done) done(); if (back) a.cancel(); };
  }
  window.addEventListener('resize', function () { placeX(); if (box.classList.contains('is-open')) strip.scrollLeft = at * strip.clientWidth; });

  function upgrade(i) { /* swap the page-size copy for the full-resolution file once it has loaded */
    var s = slides[i]; if (!s || s.__up) return; s.__up = true;
    var im = s.querySelector('img'), cur = im.getAttribute('src'), full = fullRes(cur);
    if (full === cur) return;
    var pre = new Image(); pre.onload = function () { im.src = full; }; pre.src = full;
  }
  function build() {
    strip.innerHTML = ''; slides = [];
    list.forEach(function (g, i) {
      var s = document.createElement('div'); s.className = 'pdp-lightbox__slide';
      if (isRvv(g)) { s.appendChild(rvvStatic(g, i)); strip.appendChild(s); slides.push(s); return; }
      var im = document.createElement('img'); im.className = 'pdp-lightbox__img'; im.alt = g.alt || ''; im.decoding = 'async'; im.draggable = false;
      im.src = g.currentSrc || g.src; im.style.transformOrigin = '0 0';
      im.addEventListener('load', function () { if (i === at) placeX(); });
      im.addEventListener('click', onImgClick);
      s.appendChild(im); strip.appendChild(s); slides.push(s);
    });
  }
  /* static copy of the review slide: poster frame + a clone of the card, same aspect as the slide on the page */
  function rvvStatic(g, i) {
    var c = document.createElement('div'); c.className = 'pcb-rvv pdp-lightbox__rvv';
    var r = g.getBoundingClientRect(); if (r.width && r.height) c.style.setProperty('--rvv-ar', (r.width / r.height).toFixed(4));
    var v = g.querySelector('video'), im = document.createElement('img');
    im.className = 'pdp-lightbox__rvv-poster'; im.alt = ''; im.decoding = 'async'; im.draggable = false;
    im.src = (v && (v.getAttribute('poster') || v.poster)) || '';
    im.addEventListener('load', function () { if (i === at) placeX(); });
    c.appendChild(im);
    var card = g.querySelector('.pcb-rvv__in'); if (card) c.appendChild(card.cloneNode(true));
    return c;
  }
  function setActive(i, fromScroll, fromOpen) {
    if (i === at && frameImg && !fromScroll) return;
    if (frameImg && frameImg !== media(slides[i])) { reset(); }
    at = i; frameImg = media(slides[i]);
    srcEl = list[i]; lastFocus = list[i];
    /* round 19: keep the page gallery (and its thumb rail, which centres itself) on the photo shown in the lightbox */
    try { var fig = list[i].closest('[data-gallery-item]'); if (track && fig && !fromOpen) { track.scrollLeft = fig.offsetLeft; } } catch (e) {}
    upgrade(i); upgrade(i + 1); upgrade(i - 1);
    placeX();
  }
  var sTimer = 0;
  strip.addEventListener('scroll', function () {
    window.clearTimeout(sTimer);
    /* the X rides with the photo in view while the strip moves (nearest slide), then settles on the active one */
    var near = slides[Math.max(0, Math.min(slides.length - 1, Math.round(strip.scrollLeft / Math.max(1, strip.clientWidth))))];
    if (near && media(near) !== frameImg) { var keep = frameImg; frameImg = media(near); placeX(); frameImg = keep; } else placeX();
    sTimer = window.setTimeout(function () { var i = Math.round(strip.scrollLeft / Math.max(1, strip.clientWidth)); i = Math.max(0, Math.min(slides.length - 1, i)); if (i !== at) setActive(i, true); else placeX(); }, 90);
  }, { passive: true });

  function open(img) {
    lastFocus = img; srcEl = img;
    list = galleryList(); if (!list.length) list = [img];
    at = Math.max(0, list.indexOf(img));
    build();
    box.classList.add('is-fit');
    box.hidden = false;
    void box.offsetWidth;
    var bd0 = box.querySelector('.pdp-lightbox__backdrop'); if (bd0 && !reduced) bd0.style.opacity = '0';
    box.classList.add('is-open');
    box.setAttribute('aria-hidden', 'false');
    strip.scrollLeft = at * strip.clientWidth;
    if (closeBtn) { closeBtn.classList.remove('is-placed'); closeBtn.style.left = ''; closeBtn.style.top = ''; }
    frameImg = null; setActive(at, true, true);
    if (closeBtn) closeBtn.focus({ preventScroll: true });
    lockScroll();
    var from = img.getBoundingClientRect();
    var go2 = function () { placeX(); flip(from, false, placeX); };
    var fi = innerImg(frameImg);
    if (ready(frameImg) || !fi) go2(); else fi.addEventListener('load', function once() { fi.removeEventListener('load', once); go2(); });
  }

  function close() {
    if (!box.classList.contains('is-open') || box.classList.contains('is-closing')) return;
    box.setAttribute('aria-hidden', 'true');
    unlockScroll();
    var hide = function () {
      box.classList.remove('is-open', 'is-closing', 'is-zoomed');
      if (closeBtn) closeBtn.classList.remove('is-placed');
      box.hidden = true;
      strip.innerHTML = ''; slides = []; frameImg = null;
      z.s = 1; z.x = 0; z.y = 0;
    };
    var origin = srcEl && srcEl.offsetParent !== null ? srcEl.getBoundingClientRect() : null;
    var vis = origin && origin.bottom > 0 && origin.top < window.innerHeight;
    if (reduced) { hide(); }
    else if (vis && frameImg && frameImg.animate) { box.classList.add('is-closing'); reset(); box.style.removeProperty('--lb-fade'); flip(origin, true, hide); }
    else { box.classList.remove('is-open'); window.setTimeout(hide, 220); }
    srcEl = null;
    if (lastFocus && typeof lastFocus.focus === 'function') lastFocus.focus({ preventScroll: true });
    lastFocus = null;
  }

  function go(d) { /* keyboard arrows: a smooth native scroll to the neighbour */
    if (slides.length < 2) return;
    var i = Math.max(0, Math.min(slides.length - 1, at + d));
    /* 2026-10-26: snap off while the smooth scroll runs (restored on scrollend, or after 520 ms where scrollend is missing) */
    strip.style.scrollSnapType = 'none';
    var restore = function () { strip.style.scrollSnapType = ''; strip.removeEventListener('scrollend', restore); };
    strip.addEventListener('scrollend', restore); setTimeout(restore, 520);
    strip.scrollTo({ left: i * strip.clientWidth, behavior: reduced ? 'auto' : 'smooth' });
  }

  /* ---- zoom state: translate(x,y) scale(s), transform-origin 0 0 ---- */
  var z = { s: 1, x: 0, y: 0 }, MAXZ = 4, ZOOM = 2.5;
  function apply(anim) {
    if (!frameImg) return;
    frameImg.style.transition = anim && !reduced ? 'transform .22s ease' : 'none';
    frameImg.style.transform = 'translate(' + z.x + 'px,' + z.y + 'px) scale(' + z.s + ')';
    var zoomed = z.s > 1.01;
    box.classList.toggle('is-zoomed', zoomed);
    strip.style.overflowX = zoomed ? 'hidden' : '';
  }
  function reset(anim) { z.s = 1; z.x = 0; z.y = 0; apply(anim); }
  function base() { var r = frameImg.getBoundingClientRect(); return { l: r.left - z.x, t: r.top - z.y, w: r.width / z.s, h: r.height / z.s }; }
  function clamp() {
    var b = base();
    var minX = Math.min(0, b.w - b.w * z.s), minY = Math.min(0, b.h - b.h * z.s);
    z.x = Math.max(minX, Math.min(0, z.x)); z.y = Math.max(minY, Math.min(0, z.y));
    if (z.s <= 1) { z.x = 0; z.y = 0; }
  }
  function zoomAt(px, py, s2) {
    if (!frameImg || !zoomable()) return;
    var b = base();
    s2 = Math.max(1, Math.min(MAXZ, s2));
    var ux = (px - b.l - z.x) / z.s, uy = (py - b.t - z.y) / z.s;
    z.x = px - b.l - ux * s2; z.y = py - b.t - uy * s2; z.s = s2;
    clamp();
  }

  /* ---- touch: pinch + pan + double-tap in JS; sideways = the strip's NATIVE scroll; downward drag closes ---- */
  var T = null, lastTap = { t: 0, x: 0, y: 0 }, lastTouchEnd = 0;
  function dist(a, b) { return Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY); }
  frame.addEventListener('touchstart', function (e) {
    if (!box.classList.contains('is-open')) return;
    var t = e.touches;
    if (t.length === 2) T = { mode: 'pinch', d0: dist(t[0], t[1]), s0: z.s };
    else if (t.length === 1) T = { mode: z.s > 1.01 ? 'pan' : 'free', x0: t[0].clientX, y0: t[0].clientY, zx: z.x, zy: z.y, t0: Date.now(), dx: 0, dy: 0, sl: strip.scrollLeft };
  }, { passive: true });
  frame.addEventListener('touchmove', function (e) {
    if (!T) return;
    var t = e.touches;
    if (T.mode === 'pinch' && t.length === 2) {
      e.preventDefault();
      var mx = (t[0].clientX + t[1].clientX) / 2, my = (t[0].clientY + t[1].clientY) / 2;
      zoomAt(mx, my, T.s0 * dist(t[0], t[1]) / T.d0); apply(false);
    } else if (t.length === 1) {
      T.dx = t[0].clientX - T.x0; T.dy = t[0].clientY - T.y0;
      if (T.mode === 'pan') { e.preventDefault(); z.x = T.zx + T.dx; z.y = T.zy + T.dy; clamp(); apply(false); }
      else if (T.mode === 'free' && Math.abs(T.dy) > 12 && Math.abs(T.dy) > Math.abs(T.dx) * 1.3) T.mode = 'down';
      if (T.mode === 'down' && frameImg) {
        e.preventDefault();
        frameImg.style.transition = 'none';
        frameImg.style.transform = T.dy > 0 ? 'translateY(' + T.dy + 'px)' : '';
        box.style.setProperty('--lb-fade', T.dy > 0 ? Math.max(.35, 1 - T.dy / 400) : 1);
      }
    }
  }, { passive: false });
  frame.addEventListener('touchend', function (e) {
    if (!T) return;
    var m = T; lastTouchEnd = Date.now();
    if (e.touches.length) { if (m.mode === 'pinch' && e.touches.length === 1) T = { mode: z.s > 1.01 ? 'pan' : 'free', x0: e.touches[0].clientX, y0: e.touches[0].clientY, zx: z.x, zy: z.y, t0: Date.now(), dx: 0, dy: 0 }; return; }
    T = null;
    box.style.removeProperty('--lb-fade');
    if (m.mode === 'pinch') { if (z.s < 1.05) reset(true); return; }
    if (m.mode === 'down') { if (m.dy > 90) { close(); } else reset(true); return; }
    var moved = Math.abs(m.dx) > 10 || Math.abs(m.dy) > 10;
    if (!moved && Date.now() - m.t0 < 300) {
      var now = Date.now();
      if (now - lastTap.t < 320 && Math.abs(m.x0 - lastTap.x) < 30 && Math.abs(m.y0 - lastTap.y) < 30) {
        lastTap.t = 0;
        if (z.s > 1.01) reset(true); else { zoomAt(m.x0, m.y0, ZOOM); apply(true); }
      } else {
        lastTap = { t: now, x: m.x0, y: m.y0 };
        var tgt = document.elementFromPoint(m.x0, m.y0);
        if (tgt && !tgt.closest(HIT) && z.s <= 1.01) window.setTimeout(function () { if (lastTap.t === now) close(); }, 330);
      }
    }
  });

  /* ---- mouse: click zooms at the pointer, the pointer pans, click again zooms out ---- */
  function onImgClick(e) {
    e.stopPropagation();
    if (Date.now() - lastTouchEnd < 600) return;
    if (e.currentTarget !== frameImg) return;
    if (z.s > 1.01) { reset(true); return; }
    zoomAt(e.clientX, e.clientY, ZOOM); apply(true);
  }
  frame.addEventListener('mousemove', function (e) {
    if (!frameImg || z.s <= 1.01 || Date.now() - lastTouchEnd < 600) return;
    var b = base();
    var fx = Math.max(0, Math.min(1, (e.clientX - b.l) / b.w)), fy = Math.max(0, Math.min(1, (e.clientY - b.t) / b.h));
    z.x = -fx * (b.w * z.s - b.w); z.y = -fy * (b.h * z.s - b.h); clamp(); apply(false);
  });
  frame.addEventListener('click', function (e) { if (!(e.target.closest && e.target.closest(HIT)) && Date.now() - lastTouchEnd > 600) close(); });
  frame.addEventListener('wheel', function (e) {
    if (!e.ctrlKey) return;
    e.preventDefault(); zoomAt(e.clientX, e.clientY, z.s * Math.exp(-e.deltaY / 100)); apply(false);
  }, { passive: false });

  box.querySelectorAll('[data-lightbox-close]').forEach(function (el) { el.addEventListener('click', close); });

  document.addEventListener('keydown', function (e) {
    if (!box.classList.contains('is-open')) return;
    if (e.key === 'Escape') { e.preventDefault(); close(); return; }
    if (e.key === 'ArrowRight') { e.preventDefault(); go(1); return; }
    if (e.key === 'ArrowLeft') { e.preventDefault(); go(-1); return; }
    if (e.key === 'Tab') { e.preventDefault(); if (closeBtn) closeBtn.focus(); }
  });

  /* A tap that ends a swipe must not open the overlay. */
  var downAt = 0, downT = 0, DRAG_PX = 8;
  function isTarget(n) { return n && n.matches && (n.matches(GALLERY_IMG) || isRvv(n)) && !n.closest('[data-thumb]'); }
  function rvvOf(n) { var f = n && n.closest && n.closest(RVV); return f && track && track.contains(f) ? f : null; }
  document.addEventListener('pointerdown', function (e) { if (isTarget(e.target) || rvvOf(e.target)) { downAt = track ? track.scrollLeft : 0; downT = Date.now(); } }, true);
  /* review slide: CAPTURE phase, so the tap opens the overlay before pdp.js's video click handler can pause the loop */
  document.addEventListener('click', function (e) {
    var f = rvvOf(e.target); if (!f || box.classList.contains('is-open')) return;
    if (Date.now() - downT < 1500 && Math.abs((track ? track.scrollLeft : 0) - downAt) > DRAG_PX) return;
    e.preventDefault(); e.stopPropagation();
    open(f);
  }, true);
  document.addEventListener('click', function (e) {
    var img = e.target;
    if (!isTarget(img)) return;
    if (Date.now() - downT < 1500 && Math.abs((track ? track.scrollLeft : 0) - downAt) > DRAG_PX) return;
    e.preventDefault();
    open(img);
  });
  function mark() {
    galleryList().forEach(function (img) {
      if (img.classList.contains('is-zoomable')) return;
      img.classList.add('is-zoomable');
      img.setAttribute('role', 'button');
      img.setAttribute('tabindex', '0');
      img.setAttribute('aria-label', isRvv(img) ? 'Enlarge: A review from Adam L.' : 'Enlarge image: ' + (img.alt || 'photo'));
    });
  }
  mark();
  document.addEventListener('pcb:tier', mark);
  document.addEventListener('keydown', function (e) {
    if (box.classList.contains('is-open') || !isTarget(e.target)) return;
    if (e.key !== 'Enter' && e.key !== ' ' && e.key !== 'Spacebar') return;
    e.preventDefault();
    open(e.target);
  });
})();
